import SentryClient from '@/helpers/sentry';
import {
  MICROSERVICES_AUTHORIZATION_ENUM,
  MICROSERVICES_ENUM,
  graphqlMutation,
  graphqlQuery,
  graphqlSubscription,
} from '@/helpers/apollo';
import { setRecipientViewedAt } from '@/store/mutations/westerosMutations';
import {
  getLoggedUserRecipients,
  wsRecipients,
  getBlocklist,
} from '@/store/queries/westerosQueries';
import { NotificationsState } from './state';
import { ActionTree } from 'vuex';
import RootState from '@/store/root_state';
import { ErrorHandler } from '@/helpers/errorHandler';

const SET_FEATURED_NOTIFICATIONS = 'SET_FEATURED_NOTIFICATIONS';
const SET_FEATURED_NOTIFICATION = 'SET_FEATURED_NOTIFICATION';
const SET_NOTIFICATION = 'SET_NOTIFICATION';
const SET_URL_BLOCKLIST = 'SET_URL_BLOCKLIST';

const actions: ActionTree<NotificationsState, RootState> = {
  async INIT_NOTIFICATIONS({ dispatch }): Promise<void> {
    dispatch('GET_URL_BLOCKLIST');
    dispatch('GET_FEATURED_NOTIFICATIONS');
    dispatch('LISTEN_NOTIFICATIONS');
  },
  async GET_FEATURED_NOTIFICATIONS({ commit }): Promise<void> {
    try {
      const { data } = await graphqlQuery(
        MICROSERVICES_ENUM.CROW,
        getLoggedUserRecipients,
        {
          take: 3,
          skip: 0,
          order: 'DESC',
          onlyUnseen: false,
        },
        MICROSERVICES_AUTHORIZATION_ENUM.PROFILE,
      );

      if (!data?.getLoggedUserRecipients) {
        ErrorHandler.showErrorMessage({
          error: new Error('No data returned from getLoggedUserRecipients query'),
          defaultMessage: true,
        });
        return;
      }

      const notifications = data.getLoggedUserRecipients;
      commit(SET_FEATURED_NOTIFICATIONS, notifications);
    } catch (e) {
      SentryClient.captureError(e);
    }
  },
  async GET_URL_BLOCKLIST({ commit }) {
    try {
      const { data } = await graphqlQuery(
        MICROSERVICES_ENUM.CROW,
        getBlocklist,
        {},
        MICROSERVICES_AUTHORIZATION_ENUM.PROFILE,
      );

      if (!data?.getBlocklist?.data) {
        ErrorHandler.showErrorMessage({
          error: new Error('No data returned from getBlocklist query'),
          defaultMessage: true,
        });
        return;
      }

      const blockedUrls = data.getBlocklist.data;

      commit(SET_URL_BLOCKLIST, blockedUrls);
    } catch (e) {
      SentryClient.captureError(e);
    }
  },
  async SET_RECIPIENT_VIEWED({ commit, state }, payload: any): Promise<void> {
    const { recipientId } = payload;

    try {
      const response = await graphqlMutation(MICROSERVICES_ENUM.CROW, setRecipientViewedAt, {
        recipientId,
      });

      const data = response.data.setRecipientViewedAt;

      const notification = state.featuredNotifications[recipientId];

      notification.viewedAt = data.viewedAt;

      commit(SET_FEATURED_NOTIFICATION, notification);
    } catch (e) {
      SentryClient.captureError(e);
    }
  },
  async LISTEN_NOTIFICATIONS({ commit }) {
    try {
      graphqlSubscription(
        MICROSERVICES_ENUM.CROW_WS,
        wsRecipients,
        {},
        MICROSERVICES_AUTHORIZATION_ENUM.PROFILE,
      )
        .filter((resp) => resp && resp.data && resp.data.recipientCreated)
        .map((resp) => resp.data.recipientCreated)
        .subscribe((notification) => commit(SET_NOTIFICATION, notification));
    } catch (e) {
      SentryClient.captureError(e);
    }
  },
};

export default actions;
